import { produce } from 'immer';
import _ from 'lodash';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useMount from '../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../common/widgets/Button';
import Dialog from '../../../../../common/widgets/Dialog';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { config } from '../../../config';
import { PlatformInfoContext } from '../../../contexts/PlatformInfoContext';
import { CardType, EmployeeCardStatuses } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import { EmployeeCardItemModel } from '../../../models/Cards';
import { resources } from '../../../resources/resources';
import { cardService } from '../../../services/card';
import applicationCrumbs from '../../ApplicationCrumbs';
import {
  EmptyStateMyCards,
  EmptyStateMyCardsNoCardAdded,
} from '../../Common/EmptyStates';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';
import { ReissueCardDialog } from './ReissueCardDialog';

export const cardTypeDescriptionMapping: { [key in CardType]: string } = {
  [CardType.Vacation]: 'employeeCards.cardTypes.vacation',
  [CardType.Meal]: 'employeeCards.cardTypes.meal',
  [CardType.Gift]: 'employeeCards.cardTypes.gift',
  [CardType.Culture]: 'employeeCards.cardTypes.culture',
  [CardType.Blue]: 'employeeCards.cardTypes.blue',
};

interface CardsPageState {
  cards?: EmployeeCardItemModel[];
  availableCardTypesToBuy?: CardType[];
  selectedCardToReissue?: EmployeeCardItemModel;
  showErrorPanel?: boolean;
  showPendingIssueDialog?: boolean;
}

export const CardsPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const navigateTo = useNavigate();
  const [state, setState] = useState<CardsPageState>({});
  const platformInfo = useContext(PlatformInfoContext);

  const openReissueCardDialog = (card: EmployeeCardItemModel) =>
    setState(
      produce(d => {
        d.selectedCardToReissue = card;
      }),
    );
  const closeReissueCardDialog = async () => {
    setState(
      produce(d => {
        d.selectedCardToReissue = undefined;
      }),
    );
  };

  const loadCards = async (abortController?: AbortController) => {
    const response = await cardService.getCards({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      setState({
        availableCardTypesToBuy: response.data.availableCardTypesToBuy,
        cards: response.data.cards,
      });
    } else {
      if (!abortController?.signal.aborted) {
        setState({ showErrorPanel: true });
      }
    }
  };

  const openPendingIssueDialog = () => {
    setState(
      produce(d => {
        d.showPendingIssueDialog = true;
      }),
    );
  };

  const closePendingIssueDialog = () => {
    setState(
      produce(d => {
        d.showPendingIssueDialog = false;
      }),
    );
  };

  useMount(loadCards);

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.cards]}
      />
      <section className="section-main section-actions my-account-page my-cards-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.cards)}</h1>
                <div className="page-action">
                  {!!state.availableCardTypesToBuy?.length &&
                    !state.showErrorPanel && (
                      <Button
                        onClick={() => navigateTo('/my-account/cards/buy')}
                        className="btn btn-solid btn-sm"
                        icon={<Icons.Add />}
                        disabled={!platformInfo?.isPlatformOpened}
                      >
                        <span className="btn-text">
                          {translate(resources.employeeCards.buy.addNewCard)}
                        </span>
                      </Button>
                    )}
                </div>
              </div>
              <Loader
                loading={!state.cards}
                loader={<LoadingSpinner />}
                showErrorPanel={state.showErrorPanel}
              >
                {!!state.cards && (
                  <>
                    {!platformInfo?.isPlatformOpened &&
                      (!!state.cards.length ||
                        !!state.availableCardTypesToBuy?.length) && (
                        <Notification
                          type={NotificationType.Warning}
                          messages={[
                            {
                              message: translate(
                                resources.employeeCards.platformClosedWarning,
                              ),
                            },
                          ]}
                        />
                      )}
                    {state.cards.length ? (
                      <>
                        <div className="user-cards">
                          {state.cards.map((e, idx) => (
                            <CardItem
                              key={idx}
                              cardItem={e}
                              openPendingIssueDialog={openPendingIssueDialog}
                              openReissueCardDialog={openReissueCardDialog}
                            />
                          ))}
                        </div>
                        {state.selectedCardToReissue && (
                          <ReissueCardDialog
                            closeDialog={closeReissueCardDialog}
                            card={state.selectedCardToReissue}
                          />
                        )}
                      </>
                    ) : state.availableCardTypesToBuy?.length ? (
                      <EmptyStateMyCardsNoCardAdded />
                    ) : (
                      <EmptyStateMyCards />
                    )}
                  </>
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
      {state.showPendingIssueDialog && (
        <PendingIssueDialog closeDialog={closePendingIssueDialog} />
      )}
    </>
  );
};

interface CardItemProps {
  cardItem: EmployeeCardItemModel;
  openPendingIssueDialog: () => void;
  openReissueCardDialog: (card: EmployeeCardItemModel) => void;
}

const CardItem = (props: CardItemProps) => {
  const {
    cardItem: {
      employeeCardId,
      cardSeries,
      cardTypeId,
      thumbnail,
      balance,
      canTopUp,
      canDisable,
      isIntegrationAvailable,
      canSeeHistory,
      canSeeDetails,
      statusId,
      formFactorName,
    },
    openPendingIssueDialog,
    openReissueCardDialog,
  } = props;
  const { t: translate } = useTranslation();
  const navigateTo = useNavigate();

  const onTopUpClick = () => {
    navigateTo(`/my-account/cards/top-up/${cardTypeId}`, {
      state: { cardSeries },
    });
  };

  return (
    <div className="user-card">
      <div
        className={`user-card-image ${
          statusId === EmployeeCardStatuses.PendingIssue
            ? 'user-card-image-disabled'
            : ''
        }`}
      >
        <div>
          <img src={rewriteImageUrl(thumbnail)} />
        </div>
      </div>
      <div className="user-card-info">
        <div className="user-card-name">
          <div>
            {translate(
              _.get(resources, cardTypeDescriptionMapping[cardTypeId]),
            )}
          </div>
          {canSeeDetails && (
            <span>
              {translate(resources.employeeCards.cardSeries)}: {cardSeries}
            </span>
          )}
          {statusId === EmployeeCardStatuses.PendingIssue && (
            <span className="user-card-status" onClick={openPendingIssueDialog}>
              {translate(resources.employeeCards.status.pendingIssue)}
              <Icons.InfoSmall />
            </span>
          )}
          <span className="user-card-form-factor">{formFactorName}</span>
        </div>

        {canSeeDetails && cardTypeId != CardType.Blue && (
          <div className="user-card-balance">
            {isIntegrationAvailable ? (
              `${balance} ${config.currency}`
            ) : (
              <>
                {translate(resources.employeeCards.balance)}:{' '}
                <PopoverDialog
                  className="info-tooltip"
                  placement="bottom"
                  type="hover"
                  content={
                    <div>
                      {translate(
                        resources.employeeCards.integrationNotAvailable,
                      )}
                    </div>
                  }
                >
                  <div>
                    <Icons.CardBalanceInfo />
                  </div>
                </PopoverDialog>
              </>
            )}
          </div>
        )}
      </div>

      <div className="user-card-actions">
        <Button
          disabled={!canTopUp}
          className="btn btn-outline btn-sm"
          onClick={onTopUpClick}
        >
          <span className="btn-text">
            {translate(resources.employeeCards.topUp.button)}
          </span>
        </Button>
        <button
          className="btn-link"
          disabled={!canSeeHistory}
          onClick={() =>
            navigateTo(`/my-account/cards/${employeeCardId}/history`)
          }
        >
          <span className="btn-text">
            {translate(resources.employeeCards.history)}
          </span>
        </button>
        <button
          onClick={() => openReissueCardDialog(props.cardItem)}
          className="btn-link"
          disabled={!canDisable}
        >
          <span className="btn-text">
            {translate(resources.employeeCards.reissueCard)}
          </span>
        </button>
      </div>
    </div>
  );
};

const PendingIssueDialog = (props: { closeDialog: () => void }) => {
  const { closeDialog } = props;
  const { t: translate } = useTranslation();
  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      closeOnClickOutside
      className="dialog-m"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.employeeCards.pendingIssueDialog.title)}
        </div>
        <div className="dialog-text centered">
          <p>{translate(resources.employeeCards.pendingIssueDialog.body)}</p>
        </div>
      </div>
    </Dialog>
  );
};
